/* V4 Onboarding workflow simulation scripts.
 *
 * Used by the Step 5 Get Started screen in V4OnboardingFlow. When the user
 * clicks a workflow tile, the matching script runs as a multi-turn chat
 * between the user and the selected agent. After the chat completes, the
 * `homeAction` payload is what populates the lightweight Onboarded Home.
 *
 * Each script has:
 *   - `turns`         : ordered list of bubbles (user / agent / system)
 *   - `integration`   : the tool the agent asks the user to connect mid-chat
 *   - `homeAction`    : the section + artifact to render on home post-chat
 */

import {
  Calendar, Mail, Inbox, Target, Kanban, Ticket, DollarSign, Users,
  FileText, Megaphone, Share2, BookOpen, BarChart3, ClipboardList, AlertTriangle, Check,
} from 'lucide-react';

/* ---- Tool catalogue ---- */
export const TOOLS = {
  gcal:       { id: 'gcal',       name: 'Google Calendar', logo: '/integrations/gcal.svg',     accent: '#4285F4' },
  hubspot:    { id: 'hubspot',    name: 'HubSpot',         logo: '/integrations/hubspot.svg',  accent: '#FF7A59' },
  salesforce: { id: 'salesforce', name: 'Salesforce',      logo: '/integrations/salesforce.svg', accent: '#00A1E0' },
  zendesk:    { id: 'zendesk',    name: 'Zendesk',         logo: '/integrations/zendesk.svg',  accent: '#03363D' },
  quickbooks: { id: 'quickbooks', name: 'QuickBooks',      logo: '/integrations/quickbooks.svg', accent: '#2CA01C' },
  workday:    { id: 'workday',    name: 'Workday',         logo: '/integrations/workday.svg',  accent: '#F38B00' },
  gdrive:     { id: 'gdrive',     name: 'Google Drive',    logo: '/integrations/gdrive.svg',   accent: '#1FA463' },
  gmail:      { id: 'gmail',      name: 'Gmail',           logo: '/integrations/gmail.svg',    accent: '#EA4335' },
  buffer:     { id: 'buffer',     name: 'Buffer',          logo: '/integrations/buffer.svg',   accent: '#168EEA' },
};

/* ---- Section catalogue (for OnboardedHome) ---- */
export const SECTIONS = {
  calendar:  { id: 'calendar',  label: 'Calendar',   icon: Calendar,
               lockedCopy: 'Calendar intelligence unlocks when you connect your calendar.' },
  tasks:     { id: 'tasks',     label: 'Must-Do',    icon: ClipboardList,
               lockedCopy: 'Must-do items appear when an agent kicks off your first deliverable.' },
  inbox:     { id: 'inbox',     label: 'Inbox Snapshot', icon: Inbox,
               lockedCopy: 'Inbox triage unlocks when you connect your email.' },
  campaigns: { id: 'campaigns', label: 'Campaigns',  icon: Megaphone,
               lockedCopy: 'Campaign intelligence unlocks when you connect HubSpot.' },
  pipeline:  { id: 'pipeline',  label: 'Pipeline',   icon: Kanban,
               lockedCopy: 'Pipeline intelligence unlocks when you connect your CRM.' },
  tickets:   { id: 'tickets',   label: 'Tickets',    icon: Ticket,
               lockedCopy: 'Ticket triage unlocks when you connect Zendesk.' },
  finance:   { id: 'finance',   label: 'Finance',    icon: DollarSign,
               lockedCopy: 'Cashflow intelligence unlocks when you connect QuickBooks.' },
  roster:    { id: 'roster',    label: 'Roster',     icon: Users,
               lockedCopy: 'People insights unlock when you connect Workday.' },
  social:    { id: 'social',    label: 'Social',     icon: Share2,
               lockedCopy: 'Social intelligence unlocks when you connect Buffer.' },
  // Locked-only sections used in the founder/default home.
  risks:     { id: 'risks',     label: 'Risks Flagged', icon: AlertTriangle, accent: 'red',
               lockedCopy: 'Risk surfacing unlocks once your agents have enough context across departments.' },
  approvals: { id: 'approvals', label: 'Pending Approvals', icon: Check,
               lockedCopy: 'Approvals unlock when you invite teammates — each invite gets their own seat and agents.' },
};

/* ---- Per-department home column layout (3 columns, mirrors V4 briefing).
 * For non-default depts the layout is static (their own section is column 1).
 * For default (Founder/CEO) the column layout is computed at render time
 * via getHomeColumns() since the agent-action section can be anything. ---- */
export const HOME_COLUMNS_BY_DEPT = {
  marketing: [
    { title: 'Campaigns', sectionIds: ['campaigns'] },
    { title: 'Today',     sectionIds: ['calendar', 'tasks'] },
    { title: 'Inbox',     sectionIds: ['inbox'] },
  ],
  sales: [
    { title: 'Pipeline',  sectionIds: ['pipeline'] },
    { title: 'Today',     sectionIds: ['calendar', 'tasks'] },
    { title: 'Inbox',     sectionIds: ['inbox'] },
  ],
  support: [
    { title: 'Tickets',   sectionIds: ['tickets'] },
    { title: 'Today',     sectionIds: ['calendar', 'tasks'] },
    { title: 'Inbox',     sectionIds: ['inbox'] },
  ],
  finance: [
    { title: 'Cashflow',  sectionIds: ['finance'] },
    { title: 'Today',     sectionIds: ['calendar', 'tasks'] },
    { title: 'Inbox',     sectionIds: ['inbox'] },
  ],
  people: [
    { title: 'People',    sectionIds: ['roster'] },
    { title: 'Today',     sectionIds: ['calendar', 'tasks'] },
    { title: 'Inbox',     sectionIds: ['inbox'] },
  ],
};

/* For Founder/CEO the layout literally mirrors V4 home:
 *   Col 1: Inbox + Calendar
 *   Col 2: Risks Flagged + Must-Do (both locked)
 *   Col 3: Pending Approvals (locked)
 * The agent's action lands inside one of these slots:
 *   - calendar action → Calendar in col 1 unlocks
 *   - inbox action → Inbox in col 1 unlocks
 *   - any other action → 'Must-Do' in col 2 unlocks (campaigns, deals, etc.
 *     all read as deliverables for the founder).
 */
export const getHomeColumns = (department, actionSectionId) => {
  if (department && HOME_COLUMNS_BY_DEPT[department]) return HOME_COLUMNS_BY_DEPT[department];
  return [
    { title: '', sectionIds: ['inbox', 'calendar'] },
    { title: '', sectionIds: ['risks', 'tasks'] },
    { title: '', sectionIds: ['approvals'] },
  ];
};

/* For founder/default — map an agent's homeAction.section to the section
 * actually rendered in the home (calendar/inbox stay; everything else
 * becomes 'tasks'/Must-Do). */
export const remapDefaultUnlock = (sectionId, payload) => {
  if (sectionId === 'calendar' || sectionId === 'inbox') return { sectionId, payload };
  // Convert dept-specific payloads into Must-Do task list shape.
  if (sectionId === 'campaigns') {
    return { sectionId: 'tasks', payload: { tasks: payload.campaigns.map((c) => ({
      title: c.name, subtitle: `${c.audience} · ${c.channels.join(' · ')}`, tag: c.stage, cta: 'Review',
    })) } };
  }
  if (sectionId === 'pipeline') {
    return { sectionId: 'tasks', payload: { tasks: payload.deals.map((d) => ({
      title: d.name, subtitle: `${d.stage} · ${d.value}`, tag: 'Deal', cta: d.action || 'Review',
    })) } };
  }
  if (sectionId === 'tickets') {
    return { sectionId: 'tasks', payload: { tasks: payload.tickets.map((t) => ({
      title: t.subject, subtitle: `${t.id} · ${t.status}`, tag: t.priority, cta: 'Open',
    })) } };
  }
  if (sectionId === 'finance') {
    return { sectionId: 'tasks', payload: { tasks: payload.items.map((it) => ({
      title: it.label, subtitle: it.status, tag: it.amount, cta: 'Review',
    })) } };
  }
  if (sectionId === 'roster') {
    return { sectionId: 'tasks', payload: { tasks: payload.items.map((it) => ({
      title: it.label, subtitle: it.subtitle, tag: it.status, cta: 'Open',
    })) } };
  }
  if (sectionId === 'social') {
    return { sectionId: 'tasks', payload: { tasks: payload.posts.map((p) => ({
      title: p.text, subtitle: `${p.channel} · ${p.status}`, tag: 'Post', cta: 'Review',
    })) } };
  }
  return { sectionId, payload };
};

/* ---- Per-department home layout (which sections render) — kept for legacy ---- */
export const HOME_LAYOUT_BY_DEPT = {
  marketing: ['campaigns', 'calendar', 'tasks', 'inbox'],
  sales:     ['pipeline', 'calendar', 'tasks', 'inbox'],
  support:   ['tickets', 'calendar', 'tasks', 'inbox'],
  finance:   ['finance', 'calendar', 'tasks', 'inbox'],
  people:    ['roster', 'calendar', 'tasks', 'inbox'],
  // Default — when no dept selected (founder, etc.)
  default:   ['calendar', 'tasks', 'inbox'],
};

/* ---- Script builder ----
 * Every script follows the same 13-turn arc so every demo conversation
 * builds trust before asking for access. This matters because the user is
 * brand-new and has zero trust in the product. The builder takes narrative
 * points and shapes them into the turn sequence:
 *
 *   1.  user     → prompt (the workflow title in natural language)
 *   2.  agent    → warmAck (shows empathy, no question)
 *   3.  agent    → probe (asks about current state / pain)
 *   4.  user     → userState (shares how they feel, no artifact yet)
 *   5.  agent    → empathy (relates, asks for relevant context doc)
 *   6.  user     → reply + artifact (PDF drop)
 *   7.  agent    → contextLine (confirms 'saved to memory' + brief read)
 *   8.  agent    → plan (outlines plan, ends with a check-in question)
 *   9.  user     → userConfirm (confirms / tweaks the plan)
 *   10. agent    → askConnect (asks for integration access)
 *   11. system   → {tool} connected
 *   12. agent    → postConnect (delivers)
 *   13. agent    → closer (one-liner to keep the door open)
 */
const t = {
  u: (text, artifact) => ({ role: 'user', text, ...(artifact ? { artifact } : {}) }),
  a: (text, extra = {}) => ({ role: 'agent', text, ...extra }),
  sys: (kind, payload = {}) => ({ role: 'system', kind, ...payload }),
};

const make = ({
  prompt, warmAck, probe, userState, empathy, reply, artifact,
  contextLine, plan, userConfirm, askConnect, postConnect, closer,
  tool, homeAction, homeActions,
}) => ({
  turns: [
    t.u(prompt),
    t.a(warmAck),
    t.a(probe),
    t.u(userState),
    t.a(empathy),
    t.u(reply, artifact),
    t.a(contextLine, { memory: artifact.name }),
    t.a(plan),
    t.u(userConfirm),
    t.a(askConnect, { integration: tool }),
    t.sys('connected', { tool }),
    t.a(postConnect),
    t.a(closer),
    t.u("Nope, we're good. Thanks."),
    t.a("Anytime. I'll check in end of day with how it went — if anything shifts before then, just message me."),
  ],
  integration: tool,
  // Outputs that populate the Onboarded Home. New shape: array of
  // { section, payload }. Falls back to wrapping legacy single homeAction.
  homeActions: homeActions || (homeAction ? [homeAction] : []),
});

/* ───────────────────────── LUC — Chief of Staff (Calendar) ───────────────────────── */
const luc = {
  'plan-day': make({
    prompt: 'Help me plan my day.',
    warmAck: "On it. Before I block your day, I want to make sure I'm pulling from the right places.",
    probe: "What's on your plate today, and what's weighing on you most?",
    userState: "Honestly — I feel stretched. Too many meetings, not enough real focus time.",
    empathy: 'Should I prioritise based on your Q1 OKRs, or just whatever has hard deadlines this week?',
    reply: "Use the OKRs — they're the truth for this quarter.",
    artifact: { type: 'pdf', name: 'Q1_OKRs.pdf', size: '184 KB' },
    contextLine: "Got it. I've added your OKRs to my long-term memory — every agent on your team can now reference this.",
    plan: "Here's my read: 3 deep-focus blocks (two on your OKRs, one for the decision memo), a 30-min sync with Sally on pipeline, and a 60-min personal block. That's a full day — hold any, or add more?",
    userConfirm: "Yeah, keep the personal block at 5:30 for the gym.",
    askConnect: "To schedule these around your existing meetings, I need read access to Google Calendar.",
    postConnect: "Connected. I've checked your day — no conflicts. Dropped 5 time-blocked tasks into your Must-Do — you can review them on the left.",
    closer: 'Anything else you want me to weave in?',
    tool: TOOLS.gcal,
    // Split: 4 timed events → Meetings; 1 memo writing block → Must-Do.
    homeActions: [
      { section: 'calendar', payload: { events: [
        { time: '9:00 AM',  title: 'Focus — OKR #1: Activation lift',  tag: 'OKR' },
        { time: '11:30 AM', title: 'Sales pipeline review with Sally', tag: 'Sync' },
        { time: '2:00 PM',  title: 'Focus — OKR #2: Pricing model',    tag: 'OKR' },
        { time: '5:30 PM',  title: 'Personal block — gym',             tag: 'Personal' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Decision memo — Q2 hiring plan', subtitle: '4:00 PM · 60 min', tag: 'Write' },
      ] } },
    ],
  }),
  'week-brief': make({
    prompt: 'Brief me on what changed this week.',
    warmAck: "I'll synthesise across departments. To make this useful, can you share your weekly review template?",
    probe: "Before I pull this together — what signals are you actually trying to catch each week?",
    userState: "What moved, what's slipping, and anything I should unblock early.",
    empathy: 'A template anchors the format and helps me match how you already run reviews.',
    reply: "Here's the doc we used last quarter.",
    artifact: { type: 'pdf', name: 'Weekly_Review_Template.pdf', size: '92 KB' },
    contextLine: "Saved. I'll use this format every Monday going forward.",
    plan: "Highlights: pipeline +12% WoW, support backlog up 23, two campaign experiments completed. I'll batch these into a Monday brief.",
    userConfirm: "That works. Let's lock it in.",
    askConnect: "To drop the brief on your calendar as a recurring slot, I need Google Calendar access.",
    postConnect: 'Connected. Recurring "Monday Briefing" is now blocked at 8:30 AM — first one fires next Monday.',
    closer: 'Want me to invite anyone else to it?',
    tool: TOOLS.gcal,
    homeAction: { section: 'calendar', payload: { events: [
      { time: 'Mon 8:30 AM', title: 'Weekly Briefing — Implify rollup', tag: 'Recurring' },
      { time: 'Tue 10:00 AM', title: 'Marketing experiment review', tag: 'Sync' },
      { time: 'Wed 2:00 PM', title: 'Sales pipeline standup', tag: 'Sync' },
    ] } },
  }),
  'walk-priorities': make({
    prompt: 'Walk me through my priorities.',
    warmAck: "Let's do it. To frame this right, what's your current quarterly plan?",
    probe: "Before I rank anything — what's the one outcome you'd trade everything else for this quarter?",
    userState: "Honestly, shipping v3.2 clean. Everything else can flex around it.",
    empathy: 'Once I have it, I can rank what to focus on this week vs defer.',
    reply: "Here it is.",
    artifact: { type: 'pdf', name: 'Quarterly_Plan_Q1.pdf', size: '212 KB' },
    contextLine: "Plan saved to memory. I've pulled out 3 north-star goals and 7 supporting initiatives.",
    plan: "Top 3 for this week: close the v3.2 release, finalise the pricing experiment, and unblock the Acme deal. Everything else can wait.",
    userConfirm: "Sounds right. Let's protect time for it.",
    askConnect: "I'll drop focus blocks for each on your calendar — need access to do that.",
    postConnect: "Done. 3 focus blocks scheduled across the week. The Acme deal block is back-to-back with prep time.",
    closer: 'Want me to slot a Friday review?',
    tool: TOOLS.gcal,
    homeAction: { section: 'calendar', payload: { events: [
      { time: 'Tue 9:00 AM', title: 'Focus — v3.2 release', tag: 'Priority' },
      { time: 'Wed 1:00 PM', title: 'Focus — Pricing experiment', tag: 'Priority' },
      { time: 'Thu 11:00 AM', title: 'Focus — Acme deal prep', tag: 'Priority' },
    ] } },
  }),
  'decision-memo': make({
    prompt: 'Help me co-author a decision memo.',
    warmAck: "Happy to. Memos work better when they have prior context — got a previous one I can match the voice of?",
    probe: "What decision are you wrestling with, and why is it hard?",
    userState: "Q2 headcount — whether to hire engineers or GTM. Both feel urgent.",
    empathy: 'Voice and structure are half the work. Drop in something recent.',
    reply: "Here's the last memo I wrote on hiring.",
    artifact: { type: 'pdf', name: 'Hiring_Decision_Memo.pdf', size: '156 KB' },
    contextLine: "Got your voice. Every memo I co-write will match this structure and tone.",
    plan: "Tell me the decision — I'll outline options, tradeoffs, and a recommendation. We can iterate over a 30-min slot.",
    userConfirm: "Yeah, let's work it through together.",
    askConnect: "Let me block a working session on your calendar.",
    postConnect: 'Working session scheduled — 4:00 PM today, room cleared. Memo doc shared as a working draft.',
    closer: 'Want me to invite a reviewer?',
    tool: TOOLS.gcal,
    homeActions: [
      { section: 'calendar', payload: { events: [
        { time: 'Today 4:00 PM', title: 'Decision memo working session', tag: 'Write' },
        { time: 'Tomorrow 10:00 AM', title: 'Decision memo — review pass', tag: 'Write' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Open memo draft (Q2 hiring)', subtitle: 'Working draft shared in Drive', tag: 'Write' },
      ] } },
    ],
  }),
};

/* ───────────────────────── MARSI — Marketing (HubSpot → Campaigns) ───────────────────────── */
const marsi = {
  'campaign-brief': make({
    prompt: 'Build a campaign brief from scratch.',
    warmAck: "Let's do it right. Briefs only work if they map to your audience — got a persona doc I can ground in?",
    probe: "Before I draft anything — what's the one behaviour change you're actually trying to drive?",
    userState: "Get more tier-1 ICPs to book strategy calls, honestly.",
    empathy: 'I want every line in the brief to ladder back to a real persona.',
    reply: "Yep — this is the one we built last cycle.",
    artifact: { type: 'pdf', name: 'ICP_Persona_Brief.pdf', size: '198 KB' },
    contextLine: "Saved. I'll use this persona for every campaign I draft going forward.",
    plan: "Suggesting a 3-week launch arc: tease → reveal → activate. CTA = book a strategy call. Channels: paid social + nurture email.",
    userConfirm: "Yep — that's the north star.",
    askConnect: 'To actually run the campaign and pull baseline metrics, I need HubSpot access.',
    postConnect: 'Connected. Campaign drafted in HubSpot — sequences staged, nothing live yet. You can review on the home view.',
    closer: 'Want me to A/B the subject lines?',
    tool: TOOLS.hubspot,
    homeActions: [
      { section: 'campaigns', payload: { campaigns: [
        { name: 'Q1 Activation — Tease',    stage: 'Drafted',   audience: 'Tier 1 ICP',    channels: ['Email', 'LinkedIn'] },
        { name: 'Q1 Activation — Reveal',   stage: 'Drafted',   audience: 'Tier 1 ICP',    channels: ['Email', 'Paid Social'] },
        { name: 'Q1 Activation — Activate', stage: 'Drafted',   audience: 'Hand-raisers',  channels: ['Email'] },
        { name: 'Holiday Reactivation',     stage: 'Live',      audience: 'Lapsed users',  channels: ['Email'] },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve Q1 Activation launch sequence', subtitle: '3 campaigns staged · ready to ship', tag: 'OKR' },
      ] } },
    ],
  }),
  'positioning-audit': make({
    prompt: 'Audit our positioning.',
    warmAck: "I'll stress-test it across messaging, audience fit, and competitive air-cover. What's our current narrative?",
    probe: "Where do you feel our positioning is weakest right now?",
    userState: "Feels generic. We sound like every other AI tool out there.",
    empathy: 'Drop your latest positioning doc — homepage or sales deck both work.',
    reply: "Here's our current positioning doc.",
    artifact: { type: 'pdf', name: 'Positioning_Doc.pdf', size: '174 KB' },
    contextLine: "Saved. I'll evaluate this against your ICP and 3 named competitors.",
    plan: "First read: 'category of one' framing is strong, but the ICP is buried below the fold. We should pull persona-specific proof higher.",
    userConfirm: "Right, let's get specific.",
    askConnect: "I'll stage two homepage variants in HubSpot for review — need access.",
    postConnect: "Variants drafted. Both link to your existing CTAs. Neither is live — you can flip the switch from your campaigns view.",
    closer: 'Want me to draft the press one-pager too?',
    tool: TOOLS.hubspot,
    homeActions: [
      { section: 'campaigns', payload: { campaigns: [
        { name: 'Homepage variant A — Category-led', stage: 'Variant draft', audience: 'All visitors',   channels: ['Web'] },
        { name: 'Homepage variant B — Persona-led',  stage: 'Variant draft', audience: 'All visitors',   channels: ['Web'] },
        { name: 'Q4 Brand awareness',                stage: 'Live',          audience: 'Cold audience',  channels: ['LinkedIn', 'Paid Social'] },
        { name: 'Customer story — Acme',             stage: 'Live',          audience: 'Mid-funnel',     channels: ['Email', 'Web'] },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Pick winner — Homepage A vs B', subtitle: 'Side-by-side review · go-live ready', tag: 'OKR' },
      ] } },
    ],
  }),
  'content-calendar': make({
    prompt: 'Plan a content calendar.',
    warmAck: "Let's anchor this. What does the next 6 weeks look like — any launches or events?",
    probe: "What's the overall story you want your content to tell this quarter?",
    userState: "We're the only AI that actually remembers your org. Memory as moat.",
    empathy: 'Cadence + calendar moments shape the whole calendar.',
    reply: "Here's our roadmap with launch dates.",
    artifact: { type: 'pdf', name: 'Marketing_Roadmap.pdf', size: '142 KB' },
    contextLine: "Roadmap saved. I'll align every piece to a launch beat.",
    plan: "Proposing 12 pieces across LinkedIn, blog, and email — 2 per week, weighted toward your v3.2 launch in week 4.",
    userConfirm: "Perfect — build around that.",
    askConnect: 'To stage these in HubSpot and schedule them, I need access.',
    postConnect: 'Drafted. 12 pieces queued — none scheduled yet. Review and approve from the campaigns view.',
    closer: 'Want me to commission visuals from Wright?',
    tool: TOOLS.hubspot,
    homeActions: [
      { section: 'campaigns', payload: { campaigns: [
        { name: 'Content sprint Wk 1-2 — Foundation', stage: 'Drafted', audience: 'All',         channels: ['Blog', 'LinkedIn'] },
        { name: 'Content sprint Wk 3 — Pre-launch',   stage: 'Drafted', audience: 'All',         channels: ['Email', 'LinkedIn'] },
        { name: 'Content sprint Wk 4 — Launch',       stage: 'Drafted', audience: 'All',         channels: ['Email', 'Blog'] },
        { name: 'Content sprint Wk 5-6 — Echo',       stage: 'Drafted', audience: 'All',         channels: ['LinkedIn', 'Blog'] },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve 12-piece content calendar', subtitle: '4 sprints · 6 weeks · ready to schedule', tag: 'OKR' },
      ] } },
    ],
  }),
  'launch-story': make({
    prompt: 'Workshop a launch story.',
    warmAck: "Launch stories live or die on the 'why now'. What are you shipping?",
    probe: "What about this launch scares you the most?",
    userState: "That it lands flat. v3.2 is a big deal internally, I'm worried it won't land externally.",
    empathy: 'Specs + market context — both shape the story.',
    reply: "Here's the launch one-pager.",
    artifact: { type: 'pdf', name: 'v3.2_Launch_Onepager.pdf', size: '188 KB' },
    contextLine: "Got it. Saved — I'll reference these specs through the launch.",
    plan: "Draft narrative: 'Implify finally has memory'. We lead with the user pain, then the unlock. Three asset types: launch post, demo video, ICP email.",
    userConfirm: "Let's make sure it lands.",
    askConnect: 'To stage the launch sequence in HubSpot, I need access.',
    postConnect: 'Launch sequence drafted across email + LinkedIn. Demo video is on Wright. Nothing live — review from campaigns.',
    closer: 'Want a press list to seed it?',
    tool: TOOLS.hubspot,
    homeActions: [
      { section: 'campaigns', payload: { campaigns: [
        { name: 'v3.2 Launch — Hero email',     stage: 'Drafted', audience: 'All users',  channels: ['Email'] },
        { name: 'v3.2 Launch — LinkedIn post',  stage: 'Drafted', audience: 'Public',     channels: ['LinkedIn'] },
        { name: 'v3.2 Launch — Press kit',      stage: 'Drafted', audience: 'Press list', channels: ['Email'] },
        { name: 'v3.2 Launch — Demo video',     stage: 'In flight · Wright',  audience: 'All users', channels: ['Web', 'LinkedIn'] },
      ] } },
      { section: 'calendar', payload: { events: [
        { time: 'Launch Day · Mon 9 AM', title: 'v3.2 Launch — Go-live block', tag: 'OKR' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve full v3.2 launch sequence', subtitle: '4 assets · cross-channel · ready to ship', tag: 'OKR' },
      ] } },
    ],
  }),
};

/* ───────────────────────── SALLY — Sales (Salesforce → Pipeline) ───────────────────────── */
const sally = {
  'follow-up-playbook': make({
    prompt: 'Build a follow-up playbook.',
    warmAck: "I'll codify what's working. What does your current pipeline look like — any stalled deals?",
    probe: "What's happening with your stalled deals right now — no reply, pushed out, or ghosted?",
    userState: "Mostly ghosted after pricing comes up. 5-7 deals stuck.",
    empathy: 'A pipeline export gives me real signal to design around.',
    reply: "Here's last quarter's deal data.",
    artifact: { type: 'pdf', name: 'Q4_Pipeline_Export.pdf', size: '224 KB' },
    contextLine: "Saved. I'll use these patterns to ground every follow-up I draft.",
    plan: "3-touch playbook: value reminder → social proof → close-or-out. Tuned for stalled mid-funnel deals.",
    userConfirm: "Got it, pricing is the snag. Let's tune around that.",
    askConnect: 'To deploy this against your stalled deals, I need Salesforce access.',
    postConnect: 'Connected. 7 stalled deals identified, follow-ups drafted, nothing sent. Review from your pipeline.',
    closer: 'Want me to draft the close-or-out versions too?',
    tool: TOOLS.salesforce,
    homeActions: [
      { section: 'pipeline', payload: { deals: [
        { name: 'Acme Corp',     value: '$48K', stage: 'Stalled — 14 days', action: 'Follow-up drafted' },
        { name: 'Northwind',     value: '$72K', stage: 'Stalled — 21 days', action: 'Follow-up drafted' },
        { name: 'Globex',        value: '$36K', stage: 'Stalled — 9 days',  action: 'Follow-up drafted' },
        { name: 'Hooli',         value: '$54K', stage: 'Negotiation',       action: 'On track' },
        { name: 'Wayne Ent.',    value: '$120K', stage: 'Closing',          action: 'On track' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve 7 follow-up email sends', subtitle: 'Stalled deals · ready to ship', tag: 'OKR' },
      ] } },
    ],
  }),
  'pipeline-walkthrough': make({
    prompt: 'Walk me through pipeline health.',
    warmAck: "I'll triage by stage and risk. What's your current quota and target?",
    probe: "Before I open pipeline — what's your gut say? Are you ahead or behind?",
    userState: "Behind. Late-stage looks thin and I'm not sure what's real.",
    empathy: 'Quota anchors what "healthy" means.',
    reply: "Here's our quarter plan.",
    artifact: { type: 'pdf', name: 'Sales_Plan_Q1.pdf', size: '138 KB' },
    contextLine: "Saved. I'll measure pipeline health against this plan from now on.",
    plan: "First read: you're at 1.8x coverage — light. 4 deals are slipping out of quarter. We need 2 more late-stage deals to hit.",
    userConfirm: "Let's find out what's actually real.",
    askConnect: 'I need Salesforce access to pull live pipeline and surface the slips.',
    postConnect: 'Connected. Slips highlighted in red, coverage gap quantified. View from pipeline.',
    closer: 'Want me to draft an action plan for the slips?',
    tool: TOOLS.salesforce,
    homeActions: [
      { section: 'pipeline', payload: { deals: [
        { name: 'Acme Corp',   value: '$48K',  stage: 'Late stage — slipping',  action: 'Risk flagged' },
        { name: 'Initech',     value: '$92K',  stage: 'Negotiation',            action: 'On track' },
        { name: 'Umbrella',    value: '$56K',  stage: 'Late stage — slipping',  action: 'Risk flagged' },
        { name: 'Stark Ind.',  value: '$140K', stage: 'Closing — final call',   action: 'On track' },
        { name: 'Pied Piper',  value: '$28K',  stage: 'Discovery',              action: 'On track' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Build action plan for 4 slipping deals', subtitle: 'Coverage gap = $112K · need 2 more late-stage', tag: 'Critical' },
      ] } },
    ],
  }),
  'discovery-script': make({
    prompt: 'Draft a discovery script.',
    warmAck: "I'll build it around real wins, not generic frameworks. Got a recent call recording or summary?",
    probe: "What's the biggest thing that goes wrong in discovery calls today?",
    userState: "We talk features too early. Buyer checks out before I've even framed value.",
    empathy: 'Past wins teach me what your buyer actually responds to.',
    reply: "Here are notes from our last 3 closed-wons.",
    artifact: { type: 'pdf', name: 'CW_Discovery_Notes.pdf', size: '162 KB' },
    contextLine: "Saved to memory. Future scripts will all anchor on these patterns.",
    plan: "11-question script with branching. Designed to surface budget + champion + decision criteria within 25 min.",
    userConfirm: "Yeah, we'll fix the order.",
    askConnect: 'I can attach this script to upcoming calls in Salesforce — need access.',
    postConnect: 'Connected. Script attached to your next 3 discovery calls. View from pipeline.',
    closer: 'Want me to coach you through one before you run it?',
    tool: TOOLS.salesforce,
    homeActions: [
      { section: 'calendar', payload: { events: [
        { time: 'Tue 10:00 AM', title: 'Hooli — Discovery call',     tag: 'Discovery' },
        { time: 'Wed 2:00 PM',  title: 'Wayne Ent. — Discovery call', tag: 'Discovery' },
        { time: 'Thu 11:00 AM', title: 'Stark Ind. — Discovery call', tag: 'Discovery' },
      ] } },
      { section: 'pipeline', payload: { deals: [
        { name: 'Hooli',       value: '$60K',  stage: 'Discovery scheduled', action: 'Script attached' },
        { name: 'Wayne Ent.',  value: '$45K',  stage: 'Discovery scheduled', action: 'Script attached' },
        { name: 'Stark Ind.',  value: '$84K',  stage: 'Discovery scheduled', action: 'Script attached' },
        { name: 'Acme Corp',   value: '$48K',  stage: 'Demo prep',           action: 'Pitch ready' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Practice script with Sally before Tue', subtitle: '15-min rehearsal block', tag: 'OKR' },
      ] } },
    ],
  }),
  'pitch-prep': make({
    prompt: 'Prep for an upcoming pitch.',
    warmAck: "Let's nail this. Who's the buyer and what stage are we in?",
    probe: "What's the shape of this deal — who's your champion, and what's slowing it down?",
    userState: "My champion's solid but her VP is skeptical on ROI. That's the block.",
    empathy: 'Drop their LinkedIn or any notes.',
    reply: "Here's the account brief on Acme.",
    artifact: { type: 'pdf', name: 'Acme_Account_Brief.pdf', size: '178 KB' },
    contextLine: "Saved. Acme context is in long-term memory — everyone on the team can pull it.",
    plan: "I'll structure the pitch around their 3 stated pains. Anchor on the v3.2 unlock — that's where we differentiate against Northpoint.",
    userConfirm: "Then the VP is who we pitch to. Not her.",
    askConnect: 'To pull live deal context and attach the pitch deck, I need Salesforce access.',
    postConnect: 'Connected. Pitch deck attached, 3 prep questions queued, follow-up sequence staged. View from pipeline.',
    closer: 'Want a dry-run with me first?',
    tool: TOOLS.salesforce,
    // Salesforce connected → pull a realistic pipeline snapshot (not just
    // this one deal). The pitch + follow-up calls are *meetings* on the
    // calendar; the deck review is a Must-Do. The Acme deal sits inside a
    // wider 4-deal pipeline view to show the value of connecting a CRM.
    homeActions: [
      { section: 'calendar', payload: { events: [
        { time: 'Thu 2:00 PM', title: 'Acme Corp — Pitch (VP Eng)', tag: 'Pitch' },
        { time: 'Mon 11:00 AM', title: 'Acme Corp — Post-pitch follow-up', tag: 'Sync' },
      ] } },
      { section: 'pipeline', payload: { deals: [
        { name: 'Acme Corp',         value: '$48K', stage: 'Demo · pitch Thu',     action: 'Pitch deck ready' },
        { name: 'Northwind Logistics', value: '$72K', stage: 'Negotiation · contract sent', action: 'Awaiting redline' },
        { name: 'Globex Systems',    value: '$24K', stage: 'Discovery · intro call done', action: 'Send proposal' },
        { name: 'Initech Solutions', value: '$96K', stage: 'Closing · final call Friday',  action: 'Confirm exec attendees' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Review Acme pitch deck', subtitle: '12 slides · 3 prep questions queued', tag: 'Pitch' },
      ] } },
    ],
  }),
};

/* ───────────────────────── ZIRA — Support (Zendesk → Tickets) ───────────────────────── */
const zira = {
  'triage-playbook': make({
    prompt: 'Build a triage playbook.',
    warmAck: "I'll design around your real ticket mix. Got recent ticket data I can analyse?",
    probe: "What's the single most painful kind of ticket right now?",
    userState: "Billing. Every edge case ends up on my plate and it's slow.",
    empathy: 'Volume + topic mix shapes the playbook.',
    reply: "Here's the export from last 60 days.",
    artifact: { type: 'pdf', name: 'Tickets_60d_Export.pdf', size: '294 KB' },
    contextLine: "Saved. I now know your top topics and SLA targets.",
    plan: "Top 3 categories: billing (38%), onboarding (24%), bugs (19%). Routing rules: P0 to engineer, billing to Finn, onboarding to me.",
    userConfirm: "Let's route that automatically.",
    askConnect: 'To deploy these routing rules and triage live tickets, I need Zendesk access.',
    postConnect: 'Connected. Routing rules deployed, 14 open tickets triaged. View from tickets.',
    closer: 'Want me to draft canned replies for the top 5 issues?',
    tool: TOOLS.zendesk,
    homeActions: [
      { section: 'tickets', payload: { tickets: [
        { id: '#4821', subject: 'Billing — invoice mismatch',         priority: 'P1', status: 'Routed to Finn' },
        { id: '#4830', subject: 'Onboarding — SSO setup',             priority: 'P2', status: 'Auto-replied' },
        { id: '#4845', subject: 'Bug — export timeout',               priority: 'P0', status: 'Escalated' },
        { id: '#4852', subject: 'Feature request — calendar sync',    priority: 'P2', status: 'Triaged' },
        { id: '#4859', subject: 'Account — seat addition',            priority: 'P2', status: 'Auto-replied' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve routing rules (3 categories)', subtitle: '14 open tickets re-routed · live preview', tag: 'Critical' },
      ] } },
    ],
  }),
  'feedback-walkthrough': make({
    prompt: 'Walk me through customer feedback.',
    warmAck: "I'll cluster by theme, not just sentiment. Got a feedback corpus?",
    probe: "What are you hoping to learn from this round of feedback?",
    userState: "Whether we're actually solving problems or just adding features.",
    empathy: 'Recent NPS + ticket comments work great.',
    reply: "Here's the last NPS pull and comments.",
    artifact: { type: 'pdf', name: 'NPS_Q4_Comments.pdf', size: '186 KB' },
    contextLine: "Saved. I'll quote real customer language back to you in every brief.",
    plan: "5 themes emerged. Top issue: 'memory across sessions' — quoted in 41% of detractor comments. Worth a roadmap conversation.",
    userConfirm: "Good question to anchor on.",
    askConnect: 'Want me to flag detractor tickets in Zendesk so you can reach out personally?',
    postConnect: 'Connected. 12 detractor tickets flagged. Pre-drafted outreach attached to each.',
    closer: 'Want me to brief the product team on the memory theme?',
    tool: TOOLS.zendesk,
    homeActions: [
      { section: 'tickets', payload: { tickets: [
        { id: '#4791', subject: 'NPS detractor — memory feedback',  priority: 'P2', status: 'Outreach drafted' },
        { id: '#4803', subject: 'NPS detractor — slow exports',     priority: 'P2', status: 'Outreach drafted' },
        { id: '#4818', subject: 'NPS detractor — pricing pushback', priority: 'P1', status: 'Routed to Finn' },
        { id: '#4824', subject: 'NPS detractor — onboarding gaps',  priority: 'P2', status: 'Outreach drafted' },
        { id: '#4837', subject: 'NPS promoter — feature praise',    priority: 'P2', status: 'Auto-replied' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Decide — memory theme on Q2 roadmap?', subtitle: '41% of detractors raised it · brief Stark', tag: 'OKR' },
      ] } },
    ],
  }),
  'common-sops': make({
    prompt: 'Draft SOPs for our common tickets.',
    warmAck: "Standardising helps. What are your current top recurring issues?",
    probe: "Which SOP gaps are costing your team the most time right now?",
    userState: "Refunds and plan changes — every rep handles them differently.",
    empathy: 'A few resolved tickets give me the pattern.',
    reply: "Here's a folder of resolved billing tickets.",
    artifact: { type: 'pdf', name: 'Resolved_Billing_Tickets.pdf', size: '232 KB' },
    contextLine: "Saved. SOPs going forward will match the resolution patterns I see here.",
    plan: "5 SOPs drafted: refund flow, plan downgrade, invoice correction, seat reassign, dunning recovery.",
    userConfirm: "We'll standardise those first.",
    askConnect: 'To attach these to ticket macros in Zendesk, I need access.',
    postConnect: 'Connected. SOPs deployed as macros. 9 open billing tickets auto-tagged with the right SOP.',
    closer: 'Want me to track macro usage rate?',
    tool: TOOLS.zendesk,
    homeActions: [
      { section: 'tickets', payload: { tickets: [
        { id: '#4901', subject: 'Refund request — annual plan',     priority: 'P1', status: 'SOP attached' },
        { id: '#4905', subject: 'Plan downgrade — seat count',      priority: 'P2', status: 'SOP attached' },
        { id: '#4910', subject: 'Invoice correction',               priority: 'P2', status: 'SOP attached' },
        { id: '#4914', subject: 'Seat reassignment — exit',         priority: 'P2', status: 'SOP attached' },
        { id: '#4919', subject: 'Dunning — failed renewal',         priority: 'P1', status: 'SOP attached' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve 5 SOP macros for Zendesk', subtitle: '9 open tickets auto-tagged · live preview', tag: 'OKR' },
      ] } },
    ],
  }),
  'churn-patterns': make({
    prompt: 'Identify churn-risk patterns.',
    warmAck: "I'll look for leading indicators, not just lagging. What's your churn data?",
    probe: "Who's a recent churn that still stings — and why?",
    userState: "Acme. We saw the signals but didn't act fast enough.",
    empathy: 'Past churn cohorts teach me what to watch for.',
    reply: "Here's our churn analysis from last quarter.",
    artifact: { type: 'pdf', name: 'Churn_Cohort_Q4.pdf', size: '208 KB' },
    contextLine: "Saved. I'll watch every account against these patterns from now on.",
    plan: "3 leading signals: usage drop > 30% MoM, support escalation, NPS < 5. Currently 8 active accounts hit 2+ signals.",
    userConfirm: "Let's make sure we catch the next one.",
    askConnect: 'To flag these accounts as risk in Zendesk and route to CS, I need access.',
    postConnect: 'Connected. 8 at-risk tickets created with full context. Routed to CS.',
    closer: 'Want auto-flagging on going forward?',
    tool: TOOLS.zendesk,
    homeActions: [
      { section: 'tickets', payload: { tickets: [
        { id: '#5001', subject: 'Acme Corp — churn signal x3',  priority: 'P0', status: 'CS escalation' },
        { id: '#5004', subject: 'Initech — usage drop 41%',     priority: 'P1', status: 'CS escalation' },
        { id: '#5008', subject: 'Globex — NPS detractor',       priority: 'P1', status: 'CS escalation' },
        { id: '#5012', subject: 'Wayne Ent. — support spike',   priority: 'P1', status: 'CS escalation' },
        { id: '#5017', subject: 'Pied Piper — payment failure', priority: 'P0', status: 'CS escalation' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve CS escalation playbook', subtitle: '5 at-risk accounts queued · ready to deploy', tag: 'Critical' },
      ] } },
    ],
  }),
};

/* ───────────────────────── FINN — Finance (QuickBooks → Finance) ───────────────────────── */
const finn = {
  'cashflow-walkthrough': make({
    prompt: 'Walk me through our cashflow.',
    warmAck: "Let's look at runway, burn, and AR. Got a P&L or cashflow doc?",
    probe: "What's keeping you up at night on the finance side?",
    userState: "Runway. It feels tight but I can't tell if it's actually tight.",
    empathy: 'Recent month is fine — I just need the shape.',
    reply: "Here's last month's P&L.",
    artifact: { type: 'pdf', name: 'PL_December.pdf', size: '174 KB' },
    contextLine: "Saved. P&L baseline locked in.",
    plan: "Burn $182K/mo, runway 14 months at current rate. AR is healthy except 3 invoices 60+ days late — worth $42K combined.",
    userConfirm: "Let's get you a real answer.",
    askConnect: 'To pull live data and act on AR, I need QuickBooks access.',
    postConnect: 'Connected. AR dashboard refreshed, 3 late invoices flagged with reminder drafts ready.',
    closer: 'Want me to send the reminders?',
    tool: TOOLS.quickbooks,
    homeActions: [
      { section: 'finance', payload: { items: [
        { label: 'Acme Corp — Invoice #2018',     amount: '$18,200', status: '67 days overdue' },
        { label: 'Globex — Invoice #2024',         amount: '$14,400', status: '61 days overdue' },
        { label: 'Initech — Invoice #2031',        amount: '$9,800',  status: '60 days overdue' },
        { label: 'Northwind — Invoice #2042',      amount: '$6,200',  status: '45 days overdue' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve 4 reminder sends ($48.6K AR)', subtitle: 'Tone calibrated · ready to ship', tag: 'Critical' },
      ] } },
    ],
  }),
  'budget-framework': make({
    prompt: 'Build a budget framework.',
    warmAck: "I'll design it around your actual operating model. Got last quarter's actuals?",
    probe: "What's the budget decision you keep deferring?",
    userState: "R&D vs GTM split. I know we're over-weight R&D but can't justify cutting.",
    empathy: 'Actuals make budgets honest.',
    reply: "Here's the actuals breakdown.",
    artifact: { type: 'pdf', name: 'Q4_Actuals.pdf', size: '198 KB' },
    contextLine: "Saved. Q4 actuals are now my baseline.",
    plan: "Budget allocations: G&A 18%, R&D 42%, S&M 32%, Other 8%. Calibrated to extend runway by 2 months without slowing R&D.",
    userConfirm: "Let's put numbers on it.",
    askConnect: 'To stage the budget in QuickBooks and track variance, I need access.',
    postConnect: 'Connected. Budget staged. Variance tracking turned on.',
    closer: 'Want me to alert you when any line goes 10%+ over?',
    tool: TOOLS.quickbooks,
    homeActions: [
      { section: 'finance', payload: { items: [
        { label: 'G&A budget — Q1',   amount: '$98K',  status: 'Staged' },
        { label: 'R&D budget — Q1',   amount: '$226K', status: 'Staged' },
        { label: 'S&M budget — Q1',   amount: '$172K', status: 'Staged' },
        { label: 'Other — Q1',        amount: '$44K',  status: 'Staged' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve Q1 budget framework ($540K total)', subtitle: '4 lines staged · variance tracking on', tag: 'OKR' },
      ] } },
    ],
  }),
  'reminder-template': make({
    prompt: 'Build a payment-reminder template.',
    warmAck: "Tone matters here. Got past reminders that worked?",
    probe: "What's your tone on collections — warm or firm?",
    userState: "Warm, but firm by touch 3. I don't want to burn relationships.",
    empathy: 'Past tone gives me the right voice.',
    reply: "Here's our previous reminder copy.",
    artifact: { type: 'pdf', name: 'Past_Reminder_Templates.pdf', size: '88 KB' },
    contextLine: "Saved. Tone calibrated.",
    plan: "3-touch sequence: gentle nudge → escalation w/ statement → final notice. Each tuned for your voice.",
    userConfirm: "Good tone for a template.",
    askConnect: 'To deploy this against your overdue invoices, I need QuickBooks.',
    postConnect: 'Connected. 5 overdue invoices loaded into the sequence. Touch #1 staged, awaiting your approval.',
    closer: 'Send touch #1 now or wait for your review?',
    tool: TOOLS.quickbooks,
    homeActions: [
      { section: 'finance', payload: { items: [
        { label: 'Reminder seq #1 — Touch 1 (Gentle)',    amount: '5 invoices', status: 'Awaiting approval' },
        { label: 'Reminder seq #1 — Touch 2 (Escalation)', amount: '5 invoices', status: 'Queued · auto-fires +7d' },
        { label: 'Reminder seq #1 — Touch 3 (Final)',     amount: '5 invoices', status: 'Queued · auto-fires +14d' },
        { label: 'Reminder seq #2 — In flight',           amount: '3 invoices', status: 'Touch 2 sent' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve Touch #1 — 5 reminders ($58.4K AR)', subtitle: 'Sequence staged · pending your green light', tag: 'OKR' },
      ] } },
    ],
  }),
  'close-plan': make({
    prompt: 'Plan our financial close.',
    warmAck: "I'll design a clean checklist. Got last close's notes?",
    probe: "What broke last close — what dragged the most?",
    userState: "Revrec. Always revrec. Two weeks of back-and-forth.",
    empathy: 'Past closes show me where things tend to slip.',
    reply: "Here's the post-mortem from last close.",
    artifact: { type: 'pdf', name: 'Last_Close_Postmortem.pdf', size: '146 KB' },
    contextLine: "Saved. I'll watch out for the same blockers this time.",
    plan: "11-step close plan with owners. Bottlenecks (revrec, AR aging) front-loaded. Target close in 6 business days.",
    userConfirm: "Let's front-load it this time.",
    askConnect: 'To pull live data and track the close, I need QuickBooks.',
    postConnect: 'Connected. Close plan instantiated. 3 items already auto-completed. Review from finance.',
    closer: 'Daily standup or async updates?',
    tool: TOOLS.quickbooks,
    homeActions: [
      { section: 'calendar', payload: { events: [
        { time: 'Daily 9:00 AM · 6 days', title: 'Close standup — 15 min', tag: 'Recurring' },
      ] } },
      { section: 'finance', payload: { items: [
        { label: 'Q1 Close — Day 1 of 6', amount: 'Tracking', status: 'In progress' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Day 1 — Revrec reconciliation',  subtitle: 'Owner: Finn · front-loaded',          tag: 'Critical' },
        { title: 'Day 1 — AR aging review',         subtitle: 'Auto-completed by Finn',              tag: 'OKR' },
        { title: 'Day 2 — Payroll accrual',         subtitle: 'Owner: Finn',                          tag: 'OKR' },
        { title: 'Day 3 — Expense reclassification', subtitle: 'Owner: Finn',                         tag: 'OKR' },
        { title: 'Day 4 — Final P&L review',        subtitle: 'Owner: You · sign-off needed',         tag: 'Critical' },
      ] } },
    ],
  }),
};

/* ───────────────────────── TALI — People (Workday → Roster) ───────────────────────── */
const tali = {
  'hiring-brief': make({
    prompt: 'Draft a hiring brief.',
    warmAck: "What's the role and what's a great hire look like?",
    probe: "What would make this a great hire in 90 days?",
    userState: "Shipping something real that I didn't have to spec.",
    empathy: 'Past hires for similar roles teach me your bar.',
    reply: "Here's the JD we used last time.",
    artifact: { type: 'pdf', name: 'Past_JD_Senior_Eng.pdf', size: '124 KB' },
    contextLine: "Saved. Voice and bar locked in.",
    plan: "Drafting the brief now: must-haves, nice-to-haves, leveling rubric, comp band, expected ramp.",
    userConfirm: "Solid bar. Let's write to it.",
    askConnect: 'To open the req and start tracking candidates, I need Workday access.',
    postConnect: 'Connected. Req opened, panel suggested, intake meeting on your calendar tomorrow.',
    closer: 'Want me to source candidates too?',
    tool: TOOLS.workday,
    homeActions: [
      { section: 'roster', payload: { items: [
        { label: 'Senior Engineer — Req opened',  subtitle: 'Posted to Workday',          status: 'Open' },
        { label: 'Hiring panel suggested',         subtitle: '4 interviewers',             status: 'Awaiting confirm' },
        { label: 'JD draft — final pass',          subtitle: 'Voice + leveling rubric',    status: 'Ready' },
        { label: 'Sourcing list — Top 50',         subtitle: 'LinkedIn + referrals',       status: 'In flight' },
      ] } },
      { section: 'calendar', payload: { events: [
        { time: 'Tomorrow 11:00 AM', title: 'Hiring intake — Senior Engineer', tag: 'Hiring' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve req + JD before posting', subtitle: 'Workday req in draft · ready to publish', tag: 'OKR' },
      ] } },
    ],
  }),
  'onboarding-plan': make({
    prompt: 'Plan onboarding for a new hire.',
    warmAck: "Let's design a real first-30. What's the role?",
    probe: "What's usually the first thing that slips in a new hire's first month?",
    userState: "Context. They spend week 1 drowning in Slack history.",
    empathy: 'Past onboarding plans show me what works for your team.',
    reply: "Here's the plan we used for the last hire.",
    artifact: { type: 'pdf', name: 'Onboarding_Plan_Last.pdf', size: '152 KB' },
    contextLine: "Saved. Onboarding template anchored.",
    plan: "Day 1 setup, week 1 shadowing, week 2 first PR, week 3 owner of 1 project, week 4 retro.",
    userConfirm: "Let's solve context.",
    askConnect: 'To assign onboarding tasks and track in Workday, I need access.',
    postConnect: 'Connected. Plan staged for the new hire. Day 1 tasks assigned, manager check-ins on calendar.',
    closer: 'Want me to schedule introductory coffees?',
    tool: TOOLS.workday,
    homeActions: [
      { section: 'roster', payload: { items: [
        { label: 'New hire — Onboarding plan staged', subtitle: '30 days · 18 tasks',           status: 'Ready' },
        { label: 'Day 1 setup — Auto-assigned',        subtitle: 'Laptop, accounts, Slack',      status: 'Auto-assigned' },
        { label: 'Buddy assigned — Sarah Chen',        subtitle: 'Week 1 shadowing partner',     status: 'Confirmed' },
        { label: 'Week 4 retro — scheduled',           subtitle: 'Manager + buddy + new hire',   status: 'Calendar block' },
      ] } },
      { section: 'calendar', payload: { events: [
        { time: 'Day 1 · 9:00 AM', title: 'New hire — Welcome + Day 1 setup', tag: 'Hiring' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve 30-day onboarding plan', subtitle: '18 tasks staged · Day 1 fires Monday', tag: 'OKR' },
      ] } },
    ],
  }),
  'engagement-check': make({
    prompt: 'Build an engagement check.',
    warmAck: "I'll keep it short and signal-rich. Got past pulse data?",
    probe: "What's the signal you most want to catch with this pulse?",
    userState: "Manager trust. I sense it's dipping but can't tell where.",
    empathy: 'Past pulse shows me what moved the needle.',
    reply: "Here's last quarter's pulse.",
    artifact: { type: 'pdf', name: 'Pulse_Q4.pdf', size: '92 KB' },
    contextLine: "Saved. I'll baseline against this.",
    plan: "5 questions, takes 90 seconds. Two are net new — one on workload, one on manager support.",
    userConfirm: "Let's surface it cleanly.",
    askConnect: 'To send via Workday and track responses, I need access.',
    postConnect: 'Connected. Pulse staged. Goes out Tuesday 9 AM. Auto-reminder Friday.',
    closer: 'Want me to surface anomalies vs last quarter?',
    tool: TOOLS.workday,
    homeActions: [
      { section: 'roster', payload: { items: [
        { label: 'Q1 Engagement Pulse — Staged',  subtitle: '5 questions · 90 sec',     status: 'Tue 9 AM' },
        { label: 'Auto-reminder follow-up',        subtitle: 'Auto-send Friday',         status: 'Scheduled' },
        { label: 'Manager rollup — anomaly check', subtitle: 'vs Q4 baseline',           status: 'Tracking' },
        { label: 'Action plan template — ready',   subtitle: 'For low-trust signals',    status: 'Available' },
      ] } },
      { section: 'calendar', payload: { events: [
        { time: 'Tue 9:00 AM', title: 'Q1 Engagement Pulse — Auto-send', tag: 'Recurring' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Approve 5 pulse questions', subtitle: '2 net new · workload + manager support', tag: 'OKR' },
      ] } },
    ],
  }),
  'review-prep': make({
    prompt: 'Prep for a performance review.',
    warmAck: "I'll structure it around growth, not just rating. Whose review?",
    probe: "What's your read on their quarter — ahead, on, or behind?",
    userState: "On, but the growth areas are sensitive. I want to get the framing right.",
    empathy: 'Past 1:1 notes give me real signal.',
    reply: "Here are 1:1 notes from this quarter.",
    artifact: { type: 'pdf', name: 'OneOnOne_Notes_Q4.pdf', size: '186 KB' },
    contextLine: "Saved. I have full context now.",
    plan: "Structuring around: wins, growth areas, peer signal, manager observations, next-quarter focus.",
    userConfirm: "We'll get the framing right together.",
    askConnect: 'To pull peer feedback and finalize in Workday, I need access.',
    postConnect: 'Connected. Peer feedback synthesized. Review draft staged for your review.',
    closer: 'Want me to schedule the review meeting?',
    tool: TOOLS.workday,
    homeActions: [
      { section: 'roster', payload: { items: [
        { label: 'Review draft — Staged',         subtitle: 'Awaiting your edits',         status: 'Draft ready' },
        { label: 'Peer feedback — 4 contributors', subtitle: 'Themes synthesized',          status: 'Synthesized' },
        { label: 'Growth plan template',          subtitle: 'Sensitive framing tuned',     status: 'Ready' },
        { label: 'Compensation calibration',      subtitle: 'Q4 band check',                status: 'In flight' },
      ] } },
      { section: 'calendar', payload: { events: [
        { time: 'Thu 3:00 PM', title: 'Performance review — 60 min', tag: 'Review' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Edit review draft before Thu 3 PM', subtitle: 'Wins · growth · peer signal', tag: 'Critical' },
      ] } },
    ],
  }),
};

/* ───────────────────────── STARK — Strategy (Drive → Calendar) ───────────────────────── */
const stark = {
  'swot': make({
    prompt: 'Run a SWOT with me.',
    warmAck: "Let's make this real, not generic. What's the strategic context?",
    probe: "What's the strategic threat that actually keeps you up at night?",
    userState: "Distribution. We have the product, not the reach.",
    empathy: 'Recent strategy doc grounds me.',
    reply: "Here's our latest strategy doc.",
    artifact: { type: 'pdf', name: 'Strategy_Doc_2024.pdf', size: '236 KB' },
    contextLine: "Saved. Strategy context locked.",
    plan: "Drafted SWOT — strengths in product depth, weakness in distribution. Threats: 2 well-funded competitors. Opportunity: vertical play.",
    userConfirm: "Let's name it and plan for it.",
    askConnect: "I'll save the SWOT to Drive and schedule a review session — need Drive access.",
    postConnect: 'Connected. SWOT saved to Drive, 60-min strategy review scheduled tomorrow.',
    closer: 'Want me to invite Sally and Marsi?',
    tool: TOOLS.gdrive,
    homeActions: [
      { section: 'calendar', payload: { events: [
        { time: 'Tomorrow 10:00 AM', title: 'Strategy review — SWOT walkthrough', tag: 'Strategy' },
        { time: 'Tomorrow 11:00 AM', title: 'Open follow-up block (post-SWOT)',   tag: 'Buffer' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Review SWOT memo on Drive', subtitle: 'Strengths · Weaknesses · Opportunities · Threats', tag: 'Research' },
      ] } },
    ],
  }),
  'gtm-outline': make({
    prompt: 'Outline a GTM strategy.',
    warmAck: "What are we taking to market?",
    probe: "Who's the one customer whose experience defines success for this GTM?",
    userState: "A mid-market ops lead who adopts us as their daily driver in week 1.",
    empathy: 'Product brief plus ICP doc are the inputs.',
    reply: "Here's the product brief.",
    artifact: { type: 'pdf', name: 'Product_Brief.pdf', size: '178 KB' },
    contextLine: "Saved. I have the product context now.",
    plan: "Two-track GTM: product-led for SMB, sales-led for mid-market. Anchored on the v3.2 unlock.",
    userConfirm: "Good north-star customer. Building to that.",
    askConnect: "I'll save the GTM doc to Drive and block a review session — need Drive.",
    postConnect: 'Connected. GTM saved. Review meeting on calendar Thursday.',
    closer: 'Want a metrics dashboard alongside?',
    tool: TOOLS.gdrive,
    homeActions: [
      { section: 'calendar', payload: { events: [
        { time: 'Thu 2:00 PM',  title: 'GTM strategy review',           tag: 'Strategy' },
        { time: 'Fri 10:00 AM', title: 'GTM metrics dashboard sync',    tag: 'Strategy' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Review GTM doc on Drive', subtitle: 'Two-track plan · SMB + mid-market', tag: 'Research' },
      ] } },
    ],
  }),
  'risk-map': make({
    prompt: 'Map our operational risks.',
    warmAck: "Let's separate signal from noise. What's your current risk register?",
    probe: "What's a risk you've been ignoring because it feels too abstract?",
    userState: "Vendor lock-in on our infra provider. We've let it grow.",
    empathy: 'Past register shows me your tolerance.',
    reply: "Here's our current risk register.",
    artifact: { type: 'pdf', name: 'Risk_Register.pdf', size: '142 KB' },
    contextLine: "Saved. Risk baseline locked.",
    plan: "Top 5 risks: vendor concentration, cyber, key-person, FX, regulatory. Mitigations drafted for each.",
    userConfirm: "Yeah, let's make it concrete.",
    askConnect: "I'll save the updated register to Drive and schedule a quarterly risk review — need Drive.",
    postConnect: 'Connected. Register saved. Recurring quarterly risk review on calendar.',
    closer: 'Want me to set up monitoring for any of these?',
    tool: TOOLS.gdrive,
    homeActions: [
      { section: 'calendar', payload: { events: [
        { time: 'Quarterly · Mon 11 AM', title: 'Risk review — quarterly', tag: 'Recurring' },
        { time: 'Mon 2:00 PM',           title: 'Cyber risk deep-dive',     tag: 'Strategy' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Review updated risk register on Drive', subtitle: '5 risks · mitigations drafted', tag: 'Research' },
      ] } },
    ],
  }),
  'roadmap-stress': make({
    prompt: 'Stress-test our roadmap.',
    warmAck: "Let's pressure-test it. What's the current roadmap?",
    probe: "Which quarter on the roadmap do you least believe in?",
    userState: "Q3. The enterprise tier has zero anchor customer signal yet.",
    empathy: 'Roadmap + recent retros show me where things wobble.',
    reply: "Here's the current 12-month roadmap.",
    artifact: { type: 'pdf', name: 'Roadmap_12mo.pdf', size: '198 KB' },
    contextLine: "Saved. Roadmap baseline locked.",
    plan: "3 fragile areas: Q2 platform migration (under-resourced), Q3 enterprise tier (no anchor customer), Q4 mobile (scope creep).",
    userConfirm: "Then we stress-test Q3 first.",
    askConnect: "Saving the stress-test memo to Drive and blocking a roadmap deep-dive — need Drive.",
    postConnect: 'Connected. Memo saved. 90-min deep-dive scheduled with your eng lead Wednesday.',
    closer: 'Want me to draft the migration risk plan now?',
    tool: TOOLS.gdrive,
    homeActions: [
      { section: 'calendar', payload: { events: [
        { time: 'Wed 9:00 AM',  title: 'Roadmap stress-test deep-dive',   tag: 'Strategy' },
        { time: 'Wed 11:00 AM', title: 'Migration risk planning block',   tag: 'Strategy' },
      ] } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Review stress-test memo on Drive', subtitle: '3 fragile areas · mitigations queued', tag: 'Research' },
      ] } },
    ],
  }),
};

/* ───────────────────────── DORA — Research (Drive → Tasks) ───────────────────────── */
const dora = {
  'competitor-scan': make({
    prompt: 'Research our top 3 competitors.',
    warmAck: "I'll go beyond the surface. Who's on your watchlist and what's the angle?",
    probe: "Which competitor move would actually hurt you most?",
    userState: "If Northpoint launched a memory feature. That's our whole moat.",
    empathy: 'Past competitive doc shows me what you already know.',
    reply: "Here's our last competitive teardown.",
    artifact: { type: 'pdf', name: 'Competitive_Teardown.pdf', size: '212 KB' },
    contextLine: "Saved. I'll build on what's already known.",
    plan: "Scanning across product, pricing, GTM, hiring, funding signals. Outputting a 4-page brief per competitor.",
    userConfirm: "We'll watch them first.",
    askConnect: "I'll save the briefs to Drive and queue them for review — need Drive.",
    postConnect: 'Connected. 3 briefs saved. Review tasks queued in Tasks.',
    closer: 'Want a Slack-ready summary too?',
    tool: TOOLS.gdrive,
    homeAction: { section: 'tasks', payload: { tasks: [
      { title: 'Review competitive brief: Northpoint', subtitle: '4-page deep dive', tag: 'Research', cta: 'Open' },
      { title: 'Review competitive brief: Acme.io', subtitle: '4-page deep dive', tag: 'Research', cta: 'Open' },
      { title: 'Review competitive brief: Velora', subtitle: '4-page deep dive', tag: 'Research', cta: 'Open' },
    ] } },
  }),
  'interview-synth': make({
    prompt: 'Synthesise customer interview notes.',
    warmAck: "Synthesis works best with the raw data. Got the transcripts?",
    probe: "What's the pattern you're hoping I'll surface?",
    userState: "Whether the 'memory across sessions' pain is real or just a nice-to-have.",
    empathy: 'Even rough notes work.',
    reply: "Here's the bundle from this round.",
    artifact: { type: 'pdf', name: 'Customer_Interview_Notes.pdf', size: '264 KB' },
    contextLine: "Saved. I have all 8 interviews in long-term memory.",
    plan: "5 themes, ranked by frequency + intensity. Top finding: 'memory across sessions' was unprompted in 6/8.",
    userConfirm: "Let's see what the data says.",
    askConnect: "I'll save the synthesis to Drive and queue a review task — need Drive.",
    postConnect: 'Connected. Synthesis saved. Review task queued.',
    closer: 'Want me to share with product?',
    tool: TOOLS.gdrive,
    homeAction: { section: 'tasks', payload: { tasks: [
      { title: 'Review: Customer interview synthesis (Round 3)', subtitle: '5 themes · 8 interviews', tag: 'Research', cta: 'Open' },
      { title: 'Brief product team on top finding', subtitle: 'Memory-across-sessions theme', tag: 'Research', cta: 'Schedule' },
    ] } },
  }),
  'trend-brief': make({
    prompt: 'Build a market trend brief.',
    warmAck: "What slice of the market — vertical or function?",
    probe: "What's the trend you're skeptical of but can't afford to ignore?",
    userState: "Vertical AI tools. Everyone says it's the future but I'm not convinced.",
    empathy: 'Drop your prior market doc.',
    reply: "Here's our last market view.",
    artifact: { type: 'pdf', name: 'Market_View_Q4.pdf', size: '188 KB' },
    contextLine: "Saved. Past market view anchored.",
    plan: "3 trend lines: AI consolidation accelerating, vertical SaaS premium expanding, distribution = the new moat.",
    userConfirm: "Fair — let's look at the data, not the hype.",
    askConnect: "I'll save the brief to Drive and queue your review — need Drive.",
    postConnect: 'Connected. Trend brief saved. Review task queued with executive summary on top.',
    closer: 'Want me to extract action items?',
    tool: TOOLS.gdrive,
    homeAction: { section: 'tasks', payload: { tasks: [
      { title: 'Review: Q1 Market Trend Brief', subtitle: '3 trends · executive summary', tag: 'Research', cta: 'Open' },
      { title: 'Decide — pursue vertical play?', subtitle: 'From trend brief #2', tag: 'Decision', cta: 'Schedule' },
    ] } },
  }),
  'opportunity-size': make({
    prompt: 'Help me size a new opportunity.',
    warmAck: "Let's get to TAM/SAM/SOM. What's the opportunity?",
    probe: "Gut check — how big do you think this opportunity is?",
    userState: "Honestly no idea. Could be $50M, could be $500M. That's my problem.",
    empathy: 'Any internal doc on it helps.',
    reply: "Here's our internal one-pager on the idea.",
    artifact: { type: 'pdf', name: 'Opportunity_Onepager.pdf', size: '156 KB' },
    contextLine: "Saved. I have the framing.",
    plan: "TAM $2.4B, SAM $480M, SOM $52M (3yr). Sensitivity analysis included.",
    userConfirm: "Let's put real numbers on it.",
    askConnect: "I'll save the sizing doc to Drive and queue a review — need Drive.",
    postConnect: 'Connected. Sizing saved. Review task queued with model assumptions called out.',
    closer: 'Want a Stark stress-test on the assumptions?',
    tool: TOOLS.gdrive,
    homeAction: { section: 'tasks', payload: { tasks: [
      { title: 'Review: Opportunity sizing — TAM/SAM/SOM', subtitle: 'Model + sensitivity analysis', tag: 'Research', cta: 'Open' },
      { title: 'Validate assumptions with Stark', subtitle: '30-min strategy stress-test', tag: 'Research', cta: 'Schedule' },
    ] } },
  }),
};

/* ───────────────────────── WRIGHT — Copywriting (Gmail → Tasks + Inbox) ─────────────────────────
 * When Wright connects Gmail, we want the user to feel "Gmail just got
 * connected — here's a snapshot of my real inbox". This shared dataset is
 * applied to every Wright workflow's homeActions so the inbox feels like
 * the user's actual recent emails (not workflow-generated drafts). The
 * workflow's own outputs (drafts to send / posts to review) become Tasks. */
const WRIGHT_INBOX_SNAPSHOT = [
  { from: 'Sarah Chen',         subject: 'Re: Q1 strategy alignment',         preview: 'Got time tomorrow at 3?',         tag: 'Reply' },
  { from: 'Mark Patel — Investor', subject: 'Quick check-in',                  preview: 'Hey, free for 15 min next week?', tag: 'Outreach' },
  { from: 'Acme Corp',           subject: 'Renewal terms — clarification',     preview: 'Following up on the proposal…',   tag: 'Reply' },
  { from: 'Notion',              subject: 'Workspace digest — 3 docs updated', preview: 'Your team made changes…',         tag: 'Notification' },
];

const wright = {
  'hero-rewrite': make({
    prompt: 'Rewrite our homepage hero.',
    warmAck: "Hero copy lives or dies on positioning. Got the current one?",
    probe: "What feels wrong about the current hero — vibe, message, or both?",
    userState: "Feels corporate. Like we're selling to IT, not to founders.",
    empathy: 'Plus a brand voice doc if you have one.',
    reply: "Here's our current hero and brand voice.",
    artifact: { type: 'pdf', name: 'Hero_and_Voice.pdf', size: '102 KB' },
    contextLine: "Saved. Brand voice locked into memory.",
    plan: "3 hero variants: outcome-led, pain-led, identity-led. Each with sub-headline and CTA.",
    userConfirm: "Let's fix the voice.",
    askConnect: "I'll send the variants over for your review — need Gmail to deliver.",
    postConnect: 'Connected. Variants drafted as a review task with email summary ready to send.',
    closer: 'Pick one or want to A/B all three?',
    tool: TOOLS.gmail,
    homeActions: [
      { section: 'inbox', payload: { items: WRIGHT_INBOX_SNAPSHOT } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Review hero variants — 3 drafts',           subtitle: 'Outcome / Pain / Identity', tag: 'Copy' },
        { title: 'Send hero variants email — draft ready',     subtitle: 'Stakeholder review · 4 recipients', tag: 'Send' },
      ] } },
    ],
  }),
  'linkedin-post': make({
    prompt: 'Draft a LinkedIn thought-leadership post.',
    warmAck: "What's the angle and audience?",
    probe: "What's the one thing you want your audience to remember after reading?",
    userState: "That we actually understand their chaos — not sell them tools for it.",
    empathy: 'Past posts show me your voice on LinkedIn.',
    reply: "Here are some past posts that did well.",
    artifact: { type: 'pdf', name: 'LinkedIn_Post_Archive.pdf', size: '88 KB' },
    contextLine: "Saved. I'll match your LinkedIn voice every time.",
    plan: "3 post drafts: hot take, story arc, framework breakdown. Each with hook + 3 paragraphs + CTA.",
    userConfirm: "Love that. Let's write to it.",
    askConnect: "I'll route them for your review via Gmail — need access.",
    postConnect: 'Connected. 3 drafts queued as review tasks. Email summary ready.',
    closer: 'Schedule them in a content calendar?',
    tool: TOOLS.gmail,
    homeActions: [
      { section: 'inbox', payload: { items: WRIGHT_INBOX_SNAPSHOT } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Review LinkedIn post — Hot take',     subtitle: 'Hook + 3 paragraphs', tag: 'Copy' },
        { title: 'Review LinkedIn post — Story arc',    subtitle: 'Hook + 3 paragraphs', tag: 'Copy' },
        { title: 'Review LinkedIn post — Framework',    subtitle: 'Hook + 3 paragraphs', tag: 'Copy' },
      ] } },
    ],
  }),
  'landing-page': make({
    prompt: 'Co-write our landing page.',
    warmAck: "What's the offer and the audience?",
    probe: "What objection kills most conversions on the current page?",
    userState: "'Looks like another AI tool.' We don't differentiate enough.",
    empathy: 'Past landers + ICP doc help me match the buyer.',
    reply: "Here's our ICP doc and one landing page that converted well.",
    artifact: { type: 'pdf', name: 'ICP_and_Top_Lander.pdf', size: '174 KB' },
    contextLine: "Saved. I have your converting voice.",
    plan: "Page structure: hero → 3 outcome blocks → social proof → FAQ → CTA. Drafted with your voice.",
    userConfirm: "We'll make the difference obvious.",
    askConnect: "I'll route the draft for review via Gmail — need access.",
    postConnect: 'Connected. Draft staged as review task. Email summary ready.',
    closer: 'Want me to pair it with paid social copy?',
    tool: TOOLS.gmail,
    homeActions: [
      { section: 'inbox', payload: { items: WRIGHT_INBOX_SNAPSHOT } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Review landing page draft',              subtitle: '5-section page', tag: 'Copy' },
        { title: 'Draft paid social companion ads',        subtitle: 'LinkedIn + Meta', tag: 'Copy' },
      ] } },
    ],
  }),
  'email-sequence': make({
    prompt: 'Build an email sequence outline.',
    warmAck: "What's the use case — onboarding, nurture, win-back?",
    probe: "What's the biggest drop-off in your current funnel?",
    userState: "Email 2. Opens crash, I think the hook is weak.",
    empathy: 'Past sequence performance shapes what works.',
    reply: "Here's our last nurture sequence and stats.",
    artifact: { type: 'pdf', name: 'Past_Sequence_Stats.pdf', size: '124 KB' },
    contextLine: "Saved. I see what worked and what didn't.",
    plan: "5-email arc: hook → value → social proof → soft CTA → hard CTA. Tuned to your audience.",
    userConfirm: "Let's fix the hook first.",
    askConnect: "I'll send the outline for your review via Gmail — need access.",
    postConnect: 'Connected. Outline staged as review task. Email summary ready.',
    closer: 'Want me to draft email #1 fully?',
    tool: TOOLS.gmail,
    homeActions: [
      { section: 'inbox', payload: { items: WRIGHT_INBOX_SNAPSHOT } },
      { section: 'tasks', payload: { tasks: [
        { title: 'Review email sequence outline (5 emails)', subtitle: 'Hook → CTA arc', tag: 'Copy' },
        { title: 'Send Email #1 — draft ready',              subtitle: 'Hook email · full copy · ready to send', tag: 'Send' },
      ] } },
    ],
  }),
};

/* ───────────────────────── BUZZ — Social Media (Buffer → Social) ───────────────────────── */
const buzz = {
  'summarise': make({
    prompt: 'Summarise this for me.',
    warmAck: "Send me the doc and I'll give you tweet-length, paragraph, and exec summary versions.",
    probe: "What angle do you want me to pull out — strategic, tactical, or punchy?",
    userState: "Punchy. I want something I can actually post.",
    empathy: "What's the source?",
    reply: "Here it is.",
    artifact: { type: 'pdf', name: 'Industry_Report.pdf', size: '312 KB' },
    contextLine: "Saved. Three summary formats incoming.",
    plan: "Summary done. Most punchy line: 'AI-native is the new SaaS-native — the wave already started'.",
    userConfirm: "Coming right up.",
    askConnect: "I can also turn the punchy line into a social post — need Buffer to schedule.",
    postConnect: 'Connected. Social post drafted from the summary. Queued for your review.',
    closer: 'Want all 3 summary formats saved as docs?',
    tool: TOOLS.buffer,
    homeActions: [
      { section: 'tasks', payload: { tasks: [
        { title: 'Review LinkedIn post: "AI-native is the new SaaS-native…"', subtitle: 'Drafted in Buffer', tag: 'Social' },
        { title: 'Review X post: "AI-native > SaaS-native. The wave already started."', subtitle: 'Drafted in Buffer', tag: 'Social' },
      ] } },
    ],
  }),
  'checklist': make({
    prompt: 'Build a checklist.',
    warmAck: "What for? Drop any past process docs.",
    probe: "Where do things usually slip — prep, execution, or follow-through?",
    userState: "Execution. We plan fine, then scramble the week of.",
    empathy: 'Past checklists show me your level of detail.',
    reply: "Here's a process doc for our launch.",
    artifact: { type: 'pdf', name: 'Launch_Process_Doc.pdf', size: '146 KB' },
    contextLine: "Saved. Your level of detail anchored.",
    plan: "18-item launch checklist with owners and timing. T-21 days to T+7 days post-launch.",
    userConfirm: "Let's prevent the scramble.",
    askConnect: "I'll turn the launch milestones into a social teaser arc — need Buffer.",
    postConnect: 'Connected. 4 social teaser posts drafted on launch milestones. Queued.',
    closer: 'Want them auto-scheduled or held for approval?',
    tool: TOOLS.buffer,
    homeActions: [
      { section: 'tasks', payload: { tasks: [
        { title: 'Review LinkedIn post: T-14 teaser',  subtitle: '"Something\'s coming. Here\'s why it matters."', tag: 'Social' },
        { title: 'Review X post: T-7 teaser',           subtitle: '"1 week out. We\'ve been quiet for a reason."', tag: 'Social' },
        { title: 'Review LinkedIn post: T-1 teaser',    subtitle: '"Tomorrow we ship."',                            tag: 'Social' },
        { title: 'Review X post: Launch day',           subtitle: '"It\'s live."',                                  tag: 'Social' },
      ] } },
    ],
  }),
  'think-through': make({
    prompt: 'Help me think through something.',
    warmAck: "Hit me. What's on your mind?",
    probe: "What's the tangle — is it too many options, or no clear option?",
    userState: "Too many. I'm stuck spinning between three paths.",
    empathy: 'Drop any context you already have.',
    reply: "Here's what I've been chewing on.",
    artifact: { type: 'pdf', name: 'Personal_Notes.pdf', size: '74 KB' },
    contextLine: "Saved. I have your raw thinking.",
    plan: "Reframed it as 3 separate decisions, not one. We can attack each independently.",
    userConfirm: "Let's unstick.",
    askConnect: "If you want, I can turn the takeaway into a contrarian-take post — need Buffer.",
    postConnect: 'Connected. Contrarian-take post drafted from your notes. Queued.',
    closer: 'Want to talk through any of the 3 decisions live?',
    tool: TOOLS.buffer,
    homeActions: [
      { section: 'tasks', payload: { tasks: [
        { title: 'Review LinkedIn post: Contrarian take', subtitle: '"Most strategy decisions are actually 3 decisions."', tag: 'Social' },
      ] } },
    ],
  }),
  'quick-research': make({
    prompt: 'Quick research dive.',
    warmAck: "What's the topic?",
    probe: "Are you after breadth (landscape) or depth (one thing, deep)?",
    userState: "Depth. Give me one punchy angle I can use.",
    empathy: 'Drop any prior context to anchor against.',
    reply: "Here's what I had on file.",
    artifact: { type: 'pdf', name: 'Topic_Background.pdf', size: '108 KB' },
    contextLine: "Saved. I'll build on this.",
    plan: "5 sources, 1-page synthesis, 3 punchy stats you can use anywhere.",
    userConfirm: "On it.",
    askConnect: "I'll turn the punchy stats into a social thread — need Buffer.",
    postConnect: 'Connected. Stat-led thread drafted. 5 posts queued.',
    closer: 'Want me to expand any of them into a full post?',
    tool: TOOLS.buffer,
    homeActions: [
      { section: 'tasks', payload: { tasks: [
        { title: 'Review X thread post 1: "5 numbers reshaping the space"', subtitle: 'Drafted in Buffer', tag: 'Social' },
        { title: 'Review X thread post 2: "1/ The first number…"',          subtitle: 'Drafted in Buffer', tag: 'Social' },
        { title: 'Review LinkedIn post: "5 numbers + what they mean"',      subtitle: 'Drafted in Buffer', tag: 'Social' },
      ] } },
    ],
  }),
};

/* ───────────────────────── Export ───────────────────────── */
export const SCRIPTS = { luc, marsi, sally, zira, finn, tali, stark, dora, wright, buzz };

export const getScript = (agentId, workflowId) => {
  const agentScripts = SCRIPTS[agentId];
  if (!agentScripts) return null;
  return agentScripts[workflowId] || null;
};
